import { Form } from "antd";
import { useEffect, useState } from "react";
import {
  addAssetSubtype,
  deleteAssetSubtype,
  getAssetTypes,
  updateAssetSubtype,
} from "../repository/data.repository";
import DynamicForm from "../controls/DynamicForm";
import { AssetSubtype } from "../repository/data.default";
import { AssetSubtypesForm } from "./AssetSubtypesFormDialog.default";
import InModal from "../controls/InModal";
import { modalTitle } from "../controls/InModal.default";
import ModalFooter from "../controls/ModalFooter";
import ModalDeleteConfirmation from "../controls/ModalDeleteConfirmation";
import ModalErrorAlert from "../controls/ModalErrorAlert";
import { hasFormTouched } from "../utils/hasFormChanged";
import { getProblemMessage } from "../utils/http";

export interface AssetSubtypesFormDialogProps {
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete";
  readonly initialValues?: AssetSubtype;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

const assetTypeRequest = async () => {
  const { data } = await getAssetTypes({
    searchQuery: { conditions: [] },
    pageNumber: 0,
    pageSize: 200,
  });
  return data.map((t) => ({ display: t.name, value: t.uid }));
};

function AssetSubtypesFormDialog(props: AssetSubtypesFormDialogProps) {
  const [form] = Form.useForm();
  const values = Form.useWatch<AssetSubtypesForm>([], form);
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (props.open) {
      if (
        props.initialValues &&
        (props.mode === "update" || props.mode === "delete")
      ) {
        form.setFieldsValue({
          name: props.initialValues.name,
          description: props.initialValues.description,
          assetType: props.initialValues.assetTypeUid
            ? {
                display: props.initialValues.assetTypeName,
                value: props.initialValues.assetTypeUid,
              }
            : undefined,
        });
      }
    } else {
      reset();
    }
  }, [props.open]);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then((value) => setValid(!!Object.values(value).length))
      .catch(() => setValid(false));
  }, [form, values, props.open]);

  function onSubmit(): void {
    const { name, description, assetType } = values;
    const assetTypeUid = (assetType as any)?.value;
    setLoading(true);
    setErrorMessage(undefined);
    if (props.mode === "add") {
      addAssetSubtype({ name, description, assetTypeUid })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((err) => {
          setLoading(false);
          setErrorMessage(getProblemMessage(err));
        });
    } else if (props.mode === "update") {
      if (hasFormTouched(form)) {
        updateAssetSubtype({
          uid: props.initialValues?.uid,
          rowVersion: props.initialValues?.rowVersion,
          name,
          description,
          assetTypeUid,
        })
          .then(() => {
            setLoading(false);
            props.onFinish?.();
          })
          .catch((err) => {
            setLoading(false);
            setErrorMessage(getProblemMessage(err));
          });
      } else {
        setLoading(false);
        props.onFinish?.();
      }
    } else if (props.mode === "delete") {
      deleteAssetSubtype(props.initialValues?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((err) => {
          setLoading(false);
          setErrorMessage(getProblemMessage(err));
        });
    }
  }

  function onCancel(): void {
    props.onCancel?.();
  }

  function reset(): void {
    setValid(false);
    setErrorMessage(undefined);
    setLoading(false);
    form.resetFields();
  }

  return (
    <InModal
      open={props.open}
      size={props.mode === "delete" ? "small" : "medium"}
      title={modalTitle(props.mode, "Asset Subtype", props.initialValues?.name)}
      onCancel={onCancel}
      actions={
        <ModalFooter
          mode={props.mode}
          loading={loading}
          valid={valid}
          onCancel={onCancel}
          onSubmit={onSubmit}
        />
      }
    >
      {props.mode !== "delete" && (
        <DynamicForm
          className="w-auto"
          form={form}
          schema={{
            groups: [
              {
                controls: [
                  {
                    type: "combobox",
                    name: "assetType",
                    label: "Asset Type",
                    request: assetTypeRequest,
                    rules: [{ required: true, message: "Asset type is required" }],
                  },
                  {
                    type: "textbox",
                    name: "name",
                    label: "Name",
                    rules: [{ required: true, message: "Name is required" }],
                  },
                  {
                    type: "textbox",
                    name: "description",
                    label: "Description",
                  },
                ],
              },
            ],
          }}
        />
      )}
      {props.mode === "delete" && (
        <ModalDeleteConfirmation
          entityLabel="asset subtype"
          entityName={props.initialValues?.name}
        />
      )}
      <ModalErrorAlert message={errorMessage} />
    </InModal>
  );
}

export default AssetSubtypesFormDialog;
